import type { ErrorBody } from '@familyhub/shared';
import { deviceId } from '../db';

/**
 * The HTTP client.
 *
 * Always same-origin and always relative: Caddy serves the PWA and the API from
 * one origin (API §1), so there is no base URL, no CORS and no preflight. The
 * session is an HttpOnly cookie, which means there is no token for this code to
 * hold, store or accidentally log.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** 401 is the one the app reacts to structurally: the session is gone. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * A request that never left the device. Distinguished from an ApiError because
 * being offline is a normal state for this app, not a failure: the outbox
 * exists precisely so the user never sees it.
 */
export class OfflineError extends Error {
  constructor(message = 'no connection') {
    super(message);
    this.name = 'OfflineError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    // API §1: every request carries this, and the server stamps
    // updated_by_device from it.
    'x-device-id': await deviceId(),
  };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    throw new OfflineError((err as Error).message);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = parsed as ErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unknown_error',
      body?.error?.message ?? response.statusText,
      body?.error?.details,
    );
  }

  return parsed as T;
}

export const isOffline = (err: unknown): err is OfflineError => err instanceof OfflineError;
